import Breadcrumb from "../components/Breadcrumb";
import { Phone, Mail, MapPin } from "lucide-react";
import { motion } from "framer-motion";
import emailjs from "emailjs-com";
import { useState } from "react";
import { Helmet } from "react-helmet";

export default function Contact() {

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: ""
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");

  const validateForm = () => {
    const newErrors = {};

    if (!form.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!form.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^\S+@\S+\.\S+$/.test(form.email)) {
      newErrors.email = "Enter a valid email address";
    }

    if (!form.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (!/^[0-9]{10,15}$/.test(form.phone)) {
      newErrors.phone = "Enter a valid phone number (10–15 digits)";
    }

    if (!form.message.trim()) {
      newErrors.message = "Message is required";
    } else if (form.message.length < 10) {
      newErrors.message = "Message must be at least 10 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });

    // Clear error while typing
    if (!validateForm()) return;
    setErrors({ ...errors, [e.target.name]: "" });
  };


  const sendEmail = (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);

    emailjs.send(
      "service_urjc4zt",
      "template_afwebmi",
      form,
      "Z024m0RDyCm365aKA"
    )
      .then((result) => {
        setSuccessMsg("Message sent successfully!");
        setLoading(false);
        setForm({ name: "", email: "", phone: "", message: "" });
      })
      .catch((error) => {
        setSuccessMsg("Something went wrong. Try again!");
        setLoading(false);
      });
  };


  return (
    <>

      <Helmet>
        <title>Contact Us – ApnaWebX | Build Your Website Today</title>
        <meta name="description" content="Contact ApnaWebX for website development, branding, SEO & digital services. Get fast support, project consultation and start building your brand online today." />
        <meta name="keywords" content="contact apnawebx, get website quote, digital services contact form, website support" />

        <link rel="canonical" href="https://apnawebx.com/" />

        {/* Open Graph */}
        <meta property="og:title" content="Contact ApnaWebX – Start Your Website Project" />
        <meta property="og:description" content="Get in touch for website development, branding & digital solutions." />
        <meta property="og:image" content="https://apnawebx.com/images/contact-og.jpg" />
        <meta property="og:url" content="https://apnawebx.com/contact" />




        <script type="application/ld+json">
          {`  {
          "@context": "https://schema.org",
        "@type": "Organization",
        "name": "ApnaWebX",
        "url": "https://apnawebx.com",
        "logo": "https://apnawebx.com/images/logo/apnawebxlogo.png",
        "description": "We provide website development, branding, SEO, WhatsApp automation & digital services.",
        "address": {
          "@type": "PostalAddress",
        "addressLocality": "Ahmedabad",
        "addressRegion": "Gujarat",
        "addressCountry": "IN"
  },
        "sameAs": [
        "https://www.instagram.com/apnawebx",
        "https://www.facebook.com/apnawebx"
        ]
}
        `}
        </script>

      </Helmet>



      {/* BREADCRUMB */}
      <Breadcrumb
        title="Contact Us"
        paths={[{ label: "Contact", link: null }]}
      />

      {/* CONTACT SECTION */}
      <section className="max-w-7xl mx-auto px-6 py-10 md:py-16">

        <div className="grid md:grid-cols-2 gap-12">

          {/* LEFT CONTACT INFO */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }} className="space-y-6">

            <h1 className="text-4xl font-bold text-[#855109]">
              Contact ApnaWebX
            </h1>

            <p className="text-gray-600">
              Need a website? Want degital services? We're here to help!
            </p>

            {/* CONTACT BUTTONS */}
            <div className="space-y-4">

              {/* Phone */}
              <a
                href="tel:+919998488480"
                className="flex items-center gap-4 bg-[#fff7ef] p-5 border border-[#eed7bc] rounded-xl shadow hover:shadow-md transition cursor-pointer"
              >
                <Phone className="w-6 h-6 text-[#855109]" />
                <div>
                  <h4 className="font-bold text-[#855109] text-lg">Phone</h4>
                  <p className="text-gray-700">+91 99984 88480</p>
                </div>
              </a>

              {/* Email */}
              <a
                href="mailto:support@apnawebx.com"
                className="flex items-center gap-4 bg-[#fff7ef] p-5 border border-[#eed7bc] rounded-xl shadow hover:shadow-md transition cursor-pointer"
              >
                <Mail className="w-6 h-6 text-[#855109]" />
                <div>
                  <h4 className="font-bold text-[#855109] text-lg">Email</h4>
                  <p className="text-gray-700">support@apnawebx.com</p>
                </div>
              </a>

              {/* Address */}
              <a
                href="https://maps.app.goo.gl/uuHL9vq3G58ru99a8"
                target="_blank" rel="noreferrer"
                className="flex items-center gap-4 bg-[#fff7ef] p-5 border border-[#eed7bc] rounded-xl shadow hover:shadow-md transition cursor-pointer"
              >
                <MapPin className="w-6 h-6 text-[#855109]" />
                <div>
                  <h4 className="font-bold text-[#855109] text-lg">Address</h4>
                  <p className="text-gray-700">D-301, Sumel-8, Ajitmil Cross Road, Odhav, Ahmedabad, 380023</p>
                </div>
              </a>

            </div>
          </motion.div>

          {/* RIGHT FORM */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }} className="bg-[#fff7ef] border border-[#eed7bc] shadow-xl p-8 rounded-2xl">

            <h3 className="text-2xl font-bold text-[#855109] mb-4">
              Let’s Talk About Your Project
            </h3>

            {/* <form className="space-y-5" onSubmit={sendEmail}>
              <div>
                <label className="text-gray-700">Name</label>
                <input
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  type="text"
                  className="w-full mt-1 p-3 border rounded-lg outline-none focus:border-[#855109]"
                  placeholder="Enter your name"
                  required
                />
              </div>

              <div>
                <label className="text-gray-700">Email</label>
                <input
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  type="email"
                  className="w-full mt-1 p-3 border rounded-lg outline-none focus:border-[#855109]"
                  placeholder="Enter your email"
                  required
                />
              </div>

              <div>
                <label className="text-gray-700">Phone</label>
                <input
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  type="text"
                  className="w-full mt-1 p-3 border rounded-lg outline-none focus:border-[#855109]"
                  placeholder="Enter your phone number"
                  required
                />
              </div>

              <div>
                <label className="text-gray-700">Message</label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  rows="4"
                  className="w-full mt-1 p-3 border rounded-lg outline-none focus:border-[#855109]"
                  placeholder="Write your message..."
                  required
                ></textarea>
              </div>

              <button
                type="submit"
                className="w-full py-3 bg-[#855109] text-white font-bold rounded-lg hover:bg-[#6e4208] transition disabled:bg-gray-400"
                disabled={loading}
              >
                {loading ? "Sending..." : "Send Message"}
              </button>

              {successMsg && (
                <p className="text-green-600 font-semibold text-center mt-3">
                  {successMsg}
                </p>
              )}
            </form> */}

            <form className="space-y-5" onSubmit={sendEmail}>
              {/* NAME */}
              <div>
                <label className="text-gray-700">Name</label>
                <input
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  type="text"
                  className="w-full mt-1 p-3 border rounded-lg outline-none focus:border-[#855109]"
                  placeholder="Enter your name"
                />
                {errors.name && <p className="text-red-600 text-sm">{errors.name}</p>}
              </div>

              {/* EMAIL */}
              <div>
                <label className="text-gray-700">Email</label>
                <input
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  type="email"
                  className="w-full mt-1 p-3 border rounded-lg outline-none focus:border-[#855109]"
                  placeholder="Enter your email"
                />
                {errors.email && <p className="text-red-600 text-sm">{errors.email}</p>}
              </div>

              {/* PHONE */}
              <div>
                <label className="text-gray-700">Phone</label>
                <input
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  type="text"
                  className="w-full mt-1 p-3 border rounded-lg outline-none focus:border-[#855109]"
                  placeholder="Enter your phone number"
                />
                {errors.phone && <p className="text-red-600 text-sm">{errors.phone}</p>}
              </div>

              {/* MESSAGE */}
              <div>
                <label className="text-gray-700">Message</label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  rows="4"
                  className="w-full mt-1 p-3 border rounded-lg outline-none focus:border-[#855109]"
                  placeholder="Write your message..."
                ></textarea>
                {errors.message && <p className="text-red-600 text-sm">{errors.message}</p>}
              </div>

              {/* SUBMIT BUTTON */}
              <button
                type="submit"
                className="w-full py-3 bg-[#855109] text-white font-bold rounded-lg hover:bg-[#6e4208] transition disabled:bg-gray-400"
                disabled={loading}
              >
                {loading ? "Sending..." : "Send Message"}
              </button>

              {/* SUCCESS MESSAGE */}
              {successMsg && (
                <p className="text-green-600 font-semibold text-center mt-3">
                  {successMsg}
                </p>
              )}
            </form>


          </motion.div>
        </div>
      </section>

      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}>
        <section className="w-full h-[300px] md:h-[550px] px-6 my-10 ">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3672.06606420704!2d72.62978937436753!3d23.02134641637571!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x395e87c1edffa9f1%3A0xcface792ea8b83fc!2sApnaWebX!5e0!3m2!1sen!2sin!4v1763451983744!5m2!1sen!2sin"
            allowFullScreen=""
            loading="lazy"
            className="w-full h-full border border-[#895712]"
          ></iframe>
        </section>
      </motion.div>
    </>
  );
}
